import startCase from "lodash/startCase";
import { useDispatch } from "react-redux";
import { Box, Typography } from "@mui/material";
import React, { ReactNode, useEffect, useState } from "react";

import { clientSharingReportGet } from "../../api/reports";
import { notificationAdd } from "../../actions/notification";
import useIsReactNativeWebView from "../../hooks/useIsReactNativeWebView";
import useMobileScreen from "../../hooks/useMobileScreen";
import List, { EmptyView, ListActions, Menu, MenuItem } from "../../components/OList";
import { t, tl } from "../../components/translate";
import { downloadExcel } from "../../helpers/files";
import { formatDataForExcel, formatHeaderForExcel } from "../accounts/Reports/helper";
import { getDisplayAgeFromDOB } from "../../helpers/formatters";
import { clientFullNameSelector } from "../../reducers/client";
import { getCustomerNumber } from "../Client/ClientList";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";
import ClientInfo from "../Client/ClientInfo/ClientInfo";
import ClientCreateEdit from "../Client/ClientCreateEdit";
import { SharedClient } from "../../interfaces/ClientInterface";

import "./Reports.scss";

// helper functions
const docColumns = () => [
  tl("clients.name"),
  tl("clients.age"),
  tl("clients.gender"),
  tl("clients.email"),
  tl("clients.phone"),
  tl("clients.address"),
  t("resourceCentre")
];

const docRowProcessor = (row: SharedClient) => [
  clientFullNameSelector(row),
  getDisplayAgeFromDOB(row.dob || ""),
  row.gender ? tl(`clients.gender.${row.gender}`) : "",
  row.email || "",
  row.phone || "",
  row.address || "",
  `${row.resourceCentreName} (${row.resourceCentreCity || row.resourceCentreAddress || ""})`
];

// eslint-disable-next-line import/prefer-default-export
export function ClientSharingReport(): ReactNode {
  const dispatch = useDispatch();
  const rc = useCurrentResourceCentre();
  const isMobileScreen = useMobileScreen();
  const { isRnWebView } = useIsReactNativeWebView();

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = React.useState<SharedClient[]>([]);
  const [selectedClientId, setSelectedClientId] = React.useState<number | null>(null);
  const [showClientInfoPanel, setShowClientInfoPanel] = React.useState(false);
  const [clientEditMode, setClientEditMode] = React.useState(false);

  const clientInfoHandle = () => {
    setSelectedClientId(null);
    setShowClientInfoPanel(false);
  };

  // effect hooks
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const { success, data, message } = await clientSharingReportGet({});
        if (success) {
          setReportData((data as unknown as SharedClient[]) || []);
        } else {
          dispatch(
            notificationAdd({
              id: new Date().getMilliseconds(),
              variant: "error",
              message: message || "Error while fetching client sharing data",
              autoTimeout: true
            })
          );
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        dispatch(
          notificationAdd({
            id: new Date().getMilliseconds(),
            variant: "error",
            message:
              err?.message || err?.data?.message || "Error while fetching client sharing data",
            autoTimeout: true
          })
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [dispatch]);

  return (
    <>
      {/* table */}
      <Box
        className="clientSharingReportList"
        sx={{
          width: "100%",
          overflowX: "auto"
        }}
      >
        <Box sx={{ minWidth: isMobileScreen || isRnWebView ? "1100px" : "auto" }}>
          <List
            automation="clientSharingReportList"
            data={reportData}
            rowHeight={50}
            isLoading={loading}
            defaultSortColumn="name"
            defaultSortOrder={1}
            adjustHeightToContents
            columns={[
              {
                key: "name",
                label: tl("clients.name"),
                sortable: true,
                sortFunction: (a, b) =>
                  `${a.firstName}${a.lastName}`.toLowerCase() >
                  `${b.firstName}${b.lastName}`.toLowerCase()
                    ? 1
                    : -1,
                formatter: (row: SharedClient) => (
                  <Typography
                    component="div"
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "flex-start",
                      cursor: "pointer",
                      textDecoration: "underline"
                    }}
                    title={startCase(clientFullNameSelector(row))}
                    onClick={() => {
                      setSelectedClientId(row.id);
                      setShowClientInfoPanel(true);
                    }}
                  >
                    <Box>{clientFullNameSelector(row).toUpperCase()}</Box>
                    <Box fontSize="small" color="gray">
                      {getCustomerNumber(row.customerNumber || "")}
                      {!!row.registrationNo && ` (Reg. No: ${row.registrationNo})`}
                    </Box>
                  </Typography>
                )
              },
              {
                key: "dob",
                label: tl("clients.age"),
                formatter: ({ dob }: SharedClient) => (
                  <Typography
                    className="textEllipsis"
                    title={getDisplayAgeFromDOB(dob || "") || ""}
                  >
                    {getDisplayAgeFromDOB(dob || "")}
                  </Typography>
                )
              },
              {
                key: "gender",
                label: tl("clients.gender"),
                formatter: (c: SharedClient) => (
                  <Typography>{c.gender ? tl(`clients.gender.${c.gender}`) : ""}</Typography>
                )
              },
              {
                key: "email",
                label: tl("clients.email"),
                formatter: (c: SharedClient) => (
                  <Typography className="textEllipsis" title={c.email || ""}>
                    {c.email}
                  </Typography>
                )
              },
              {
                key: "phone",
                label: tl("clients.phone"),
                formatter: (c: SharedClient) => (
                  <Typography className="textEllipsis" title={c.phone || ""}>
                    {c.phone}
                  </Typography>
                )
              },
              {
                key: "address",
                label: tl("clients.address"),
                formatter: (row: SharedClient) => (
                  <Typography className="textEllipsis" title={row.address || ""}>
                    {row.address}
                  </Typography>
                )
              },
              {
                key: "resourceCentre",
                segmentable: true,
                label: tl("resourceCentre"),
                formatter: (row: SharedClient) => (
                  <Typography>
                    {row.resourceCentreName}
                    <Box component="span" color="gray" fontSize="small" display="block">
                      {`${row.resourceCentreCity || ""}, ${row.resourceCentreAddress || ""}`}
                    </Box>
                  </Typography>
                )
              }
            ]}
          >
            <EmptyView>
              <Box textAlign="center" padding="50px">
                No items to show.
              </Box>
            </EmptyView>
            <ListActions>
              {(props: { getProcessedData: () => SharedClient[] }) => (
                <Menu>
                  <MenuItem
                    onClick={async () => {
                      try {
                        await downloadExcel(
                          "Client Sharing Report",
                          formatHeaderForExcel(docColumns()),
                          formatDataForExcel(props.getProcessedData(), docRowProcessor),
                          rc
                        );
                      } catch (err) {
                        dispatch(
                          notificationAdd({
                            id: new Date().getMilliseconds(),
                            variant: "error",
                            message: "Failed to download Excel.",
                            autoTimeout: true
                          })
                        );
                      }
                    }}
                  >
                    {tl("reports.excel")}
                  </MenuItem>
                </Menu>
              )}
            </ListActions>
          </List>
        </Box>
      </Box>

      {/* panels and dialogs */}
      <>
        {showClientInfoPanel &&
          (clientEditMode ? (
            <ClientCreateEdit
              setEditMode={setClientEditMode}
              clientId={selectedClientId || undefined}
              mode="edit"
              onCancel={() => {
                setClientEditMode(false);
              }}
              stayOnCurrentPage
            />
          ) : (
            <ClientInfo
              id={selectedClientId || 0}
              handleViewClose={clientInfoHandle}
              stayOnCurrentPage
              setEditMode={setClientEditMode}
            />
          ))}
      </>
    </>
  );
}
